import type { VideoSummary } from "./api.ts";

const KEY = "aiox.state.v1";

export interface PlaybackRecord {
  provider: string;
  videoId: string;
  title: string;
  thumbnail?: string;
  position: number;
  duration: number;
  updatedAt: number;
}

export type ThemeSetting = "light" | "dark" | "system";
export type Density = "compact" | "comfortable" | "roomy";
export type Skin = "a" | "b" | "c";
export type ReduceMotion = "system" | "always" | "never";
export type Quality = "auto" | "1080" | "720" | "480";

export interface Settings {
  theme: ThemeSetting;
  /** Accent skin. The dark wall is the only theme; the accent is the choice. */
  skin: Skin;
  /** Set once the reader picks an accent, so the default never overrides them. */
  skinPicked?: boolean;
  density: Density;
  reduceMotion: ReduceMotion;
  autoplay: boolean;
  quality: Quality;
  muted: boolean;
  resume: boolean;
  blurArtwork: boolean;
  disabledProviders: string[];
  /** Categories hidden everywhere: listings, feed, search and the catalogue. */
  hiddenCategories: string[];
  /** Categories painted on the home feed. Empty means "every visible one". */
  feedCategories: string[];
  /** Providers painted on the home feed, in the order chosen. Empty means "every working one". */
  feedProviders: string[];
  /** Providers each home row may draw from, by category id. Absent means "all". */
  feedRowSites: Record<string, string[]>;
  /** Whether the home page opens with the Spotlight band. */
  spotlightEnabled: boolean;
  /** Categories included in a fan-out search. Empty means "every visible one". */
  searchCategories: string[];
}

export interface AppState {
  favorites: Record<string, PlaybackRecord>;
  history: Record<string, PlaybackRecord>;
  playlists: { id: string; name: string; items: PlaybackRecord[] }[];
  settings: Settings;
  gateAccepted: boolean;
  /** Last queries that returned results, newest first. */
  recentQueries: string[];
}

const DEFAULTS: AppState = {
  favorites: {},
  history: {},
  playlists: [],
  settings: {
    theme: "light",
    skin: "c",
    density: "comfortable",
    reduceMotion: "system",
    autoplay: true,
    quality: "auto",
    muted: false,
    resume: true,
    blurArtwork: false,
    disabledProviders: [],
    hiddenCategories: ["gay", "trans"],
    feedCategories: [],
    feedProviders: [],
    feedRowSites: {},
    spotlightEnabled: true,
    searchCategories: [],
  },
  gateAccepted: false,
  recentQueries: [],
};

function read(): AppState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return structuredClone(DEFAULTS);
    const parsed = JSON.parse(raw) as Partial<AppState> & {
      settings?: Partial<Settings> & { defaultQuality?: string; theme?: string };
    };
    const stored: Partial<Settings> & { defaultQuality?: string; theme?: string } =
      parsed.settings ?? {};
    const settings: Settings = { ...structuredClone(DEFAULTS.settings), ...stored };
    // v1 stored a provider-level preference and only a light theme.
    if (!("quality" in stored) && stored.defaultQuality) {
      settings.quality = stored.defaultQuality === "lowest" ? "480" : "auto";
    }
    if (stored.theme !== "light" && stored.theme !== "dark" && stored.theme !== "system") {
      settings.theme = "light";
    }
    if (!Array.isArray(settings.feedProviders)) settings.feedProviders = [];
    if (!settings.feedRowSites || typeof settings.feedRowSites !== "object") settings.feedRowSites = {};
    if (typeof settings.spotlightEnabled !== "boolean") settings.spotlightEnabled = true;
    if (!Array.isArray(settings.feedCategories)) settings.feedCategories = [];
    delete (settings as Settings & { defaultQuality?: string }).defaultQuality;
    // Gold is the house accent. A profile written before the accent existed
    // has no marker, so it takes the default once; after a deliberate pick the
    // marker keeps that choice.
    if (!parsed.settings?.skinPicked) {
      settings.skin = DEFAULTS.settings.skin;
    }
    return {
      ...structuredClone(DEFAULTS),
      ...parsed,
      settings,
    };
  } catch {
    return structuredClone(DEFAULTS);
  }
}

let state = read();

function persist(): void {
  localStorage.setItem(KEY, JSON.stringify(state));
}

export function getState(): AppState {
  return state;
}

const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

/** Mirror the settings onto the document so CSS never branches per rule. */
export function applyAppearance(): void {
  const root = document.documentElement;
  /* One theme: the dark wall. The theme setting stays in stored state so an
     older profile still loads, but it no longer has a branch to drive. */
  root.dataset.theme = "dark";
  root.dataset.skin = state.settings.skin;
  root.dataset.density = state.settings.density;
  root.dataset.blur = String(state.settings.blurArtwork);
  root.dataset.motion =
    state.settings.reduceMotion === "always"
      ? "reduced"
      : state.settings.reduceMotion === "never"
        ? "full"
        : reducedQuery.matches
          ? "reduced"
          : "system";
}

reducedQuery.addEventListener("change", applyAppearance);
applyAppearance();

/** Categories that are adult-adjacent enough to be opt-in rather than default. */
export const SENSITIVE_CATEGORIES = ["gay", "trans"] as const;

export function isCategoryVisible(category: string): boolean {
  return !state.settings.hiddenCategories.includes(category);
}

export function setCategoryVisible(category: string, visible: boolean): void {
  const hidden = new Set(state.settings.hiddenCategories);
  if (visible) hidden.delete(category);
  else hidden.add(category);
  state.settings.hiddenCategories = [...hidden];
  persist();
}

/** Categories the home feed may paint. */
export function feedCategories(): string[] {
  const chosen = state.settings.feedCategories;
  return (chosen.length > 0 ? chosen : []).filter(isCategoryVisible);
}

/** Categories a fan-out search may query. */
export function searchCategories(): string[] {
  const chosen = state.settings.searchCategories;
  return (chosen.length > 0 ? chosen : []).filter(isCategoryVisible);
}

export function setFeedCategories(categories: string[]): void {
  state.settings.feedCategories = [...categories];
  persist();
}

/**
 * Providers the home feed may paint, in the order they were chosen. An empty
 * list means "every working provider", which is also the default.
 */
export function feedProviders(): string[] {
  const disabled = new Set(state.settings.disabledProviders);
  return state.settings.feedProviders.filter((id) => !disabled.has(id));
}

export function feedRowSites(): Record<string, string[]> {
  return getState().settings.feedRowSites ?? {};
}

export function setFeedRowSites(next: Record<string, string[]>): void {
  updateSettings({ feedRowSites: next });
}

export function setFeedProviders(providers: string[]): void {
  state.settings.feedProviders = [...new Set(providers)];
  persist();
}

export function setSearchCategories(categories: string[]): void {
  state.settings.searchCategories = [...categories];
  persist();
}

/** Rows the feed should render: the chosen subset, else every visible one. */
export function feedCategoryIds(all: string[]): string[] {
  const chosen = feedCategories();
  const visible = all.filter(isCategoryVisible);
  if (chosen.length === 0) return visible;
  return visible.filter((category) => chosen.includes(category));
}

export function searchCategoryIds(all: string[]): string[] {
  const chosen = searchCategories();
  const visible = all.filter(isCategoryVisible);
  if (chosen.length === 0) return visible;
  return visible.filter((category) => chosen.includes(category));
}

export function videoKey(provider: string, videoId: string): string {
  return `${provider}:${videoId}`;
}

export function isFavorite(provider: string, videoId: string): boolean {
  return Boolean(state.favorites[videoKey(provider, videoId)]);
}

export function toggleFavorite(video: PlaybackRecord): boolean {
  const key = videoKey(video.provider, video.videoId);
  if (state.favorites[key]) {
    delete state.favorites[key];
    persist();
    return false;
  }
  state.favorites[key] = video;
  persist();
  return true;
}

export function recordProgress(video: PlaybackRecord): void {
  const key = videoKey(video.provider, video.videoId);
  const existing = state.history[key];
  state.history[key] = { ...video, updatedAt: Date.now() };
  if (existing) delete state.history[key];
  persist();
}

export function continueWatching(): PlaybackRecord[] {
  return Object.values(state.history)
    .filter((entry) => entry.duration > 0 && entry.position / entry.duration < 0.97)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 20);
}

export function recentHistory(): PlaybackRecord[] {
  return Object.values(state.history)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 60);
}

export function favoriteList(): PlaybackRecord[] {
  return Object.values(state.favorites).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function addToPlaylist(playlistId: string, video: PlaybackRecord): void {
  const playlist = state.playlists.find((entry) => entry.id === playlistId);
  if (!playlist) return;
  const key = videoKey(video.provider, video.videoId);
  if (playlist.items.some((item) => videoKey(item.provider, item.videoId) === key)) return;
  playlist.items.unshift(video);
  persist();
}

export function createPlaylist(name: string): string {
  const id = `pl_${Date.now().toString(36)}`;
  state.playlists.unshift({ id, name, items: [] });
  persist();
  return id;
}

const QUERY_LIMIT = 6;

export function recordQuery(query: string): void {
  const trimmed = query.trim().slice(0, 80);
  if (trimmed.length < 2) return;
  const rest = state.recentQueries.filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase());
  state.recentQueries = [trimmed, ...rest].slice(0, QUERY_LIMIT);
  persist();
}

export function recentQueries(): string[] {
  return state.recentQueries;
}

export function clearRecentQueries(): void {
  state.recentQueries = [];
  persist();
}

export function updateSettings(patch: Partial<AppState["settings"]>): void {
  state.settings = { ...state.settings, ...patch };
  persist();
  applyAppearance();
  document.dispatchEvent(new CustomEvent("aiox:settings"));
}

export function acceptGate(): void {
  state.gateAccepted = true;
  persist();
}

export function toPlaybackRecord(
  video: VideoSummary,
  position = 0,
  duration = 0,
): PlaybackRecord {
  return {
    provider: video.provider,
    videoId: video.id,
    title: video.title,
    thumbnail: video.thumbnail,
    position,
    duration,
    updatedAt: Date.now(),
  };
}
